/**
 * openPreview — the Preview button. Builds the site exactly as publishing
 * would (buildSiteFiles, the same renderer), writes it as a new immutable
 * revision in this device's OPFS, and shows it in a tab served by the preview
 * service worker. Nothing leaves the device.
 *
 * The tab is opened synchronously, inside the click, on a "building" page
 * (so no popup blocker gets in the way of an asynchronous build); that page
 * waits for this build's result and then moves to its revision.
 */
import type * as Y from "yjs";
import { OpfsBlobStore } from "@website-builder/blob-store/opfs";
import type { SiteThemeOrLegacy } from "@website-builder/schemas";

import { buildSiteFiles, serializeForSite } from "../publish/buildSiteFiles.js";
import {
  revisionOf,
  switchTo,
  writeBuildResult,
  writeRevision,
  type PreviewFile,
} from "./previewStore.js";
import {
  buildingUrl,
  channelName,
  previewBase,
  type SwitchedMessage,
} from "./protocol.js";
import type { ActiveDesignSystem } from "../designSystem/designSystemFlows.js";
import { customRenderFor } from "../designSystem/prerender.js";

export interface OpenPreviewInput {
  readonly ydoc: Y.Doc;
  readonly siteId: string;
  readonly siteName: string;
  readonly theme?: SiteThemeOrLegacy;
  /** The site's active design system (absent or Duro: render in Duro). */
  readonly designSystem?: ActiveDesignSystem;
}

export interface OpenedPreview {
  /** The tab, or null when the browser refused to open one. */
  readonly tab: Window | null;
  /** The building page's URL (to offer as a link when `tab` is null). */
  readonly url: string;
  /** Settles with the revision shown, or rejects with why the build failed. */
  readonly done: Promise<string>;
}

// Stands in for the revision id while the files are rendered: the id is the
// hash of those files, which contain their own base URL.
const REV_PLACEHOLDER = "wb-preview-rev-placeholder";

/** What a preview needs of the built files: the pages and their assets. */
const servable = (path: string) => /\.(html|css|js)$/.test(path);

const token = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) =>
    b.toString(36).padStart(2, "0"),
  )
    .join("")
    .slice(0, 20);

/** Must be called from the click handler itself (it opens the tab). */
export function openPreview(input: OpenPreviewInput): OpenedPreview {
  const build = token();
  const url = buildingUrl(input.siteId, build);
  const tab = window.open(url, "_blank");
  const store = new OpfsBlobStore(["sites", input.siteId, "_preview"]);

  const done = (async () => {
    try {
      const siteInput = {
        ydoc: input.ydoc,
        siteId: input.siteId,
        siteName: input.siteName,
        ...(input.theme ? { theme: input.theme } : {}),
        baseUrl: previewBase(input.siteId, REV_PLACEHOLDER),
      };
      const render = await customRenderFor(
        input.designSystem,
        () => serializeForSite(siteInput).doc,
      );
      if (!render.ok) throw new Error(render.error);
      const { files } = buildSiteFiles({
        ...siteInput,
        ...(render.custom ? { customDesignSystem: render.custom } : {}),
      });
      const drafts: PreviewFile[] = files.filter((f) => servable(f.path));
      const rev = await revisionOf(drafts);
      const final = drafts.map((f) => ({
        path: f.path,
        content: f.content.replaceAll(REV_PLACEHOLDER, rev),
      }));
      await writeRevision(store, final, rev);
      await switchTo(store, rev);
      await writeBuildResult(store, build, { ok: true, rev });
      const channel = new BroadcastChannel(channelName(input.siteId));
      channel.postMessage({ type: "switched", rev } satisfies SwitchedMessage);
      channel.close();
      return rev;
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      // The waiting tab shows it; any older preview stays as it was.
      await writeBuildResult(store, build, { ok: false, error }).catch(
        () => undefined,
      );
      throw e;
    }
  })();
  return { tab, url, done };
}
